import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@mantine/core";
import apiService from "../../services/api";

const TrainerPaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const trainerId = searchParams.get("trainer_id");
    const paymentSuccess = searchParams.get("payment") === "success";

    if (!trainerId || !paymentSuccess) {
      setVerifying(false);
      setError("Missing payment confirmation details. Please contact support.");
      return;
    }

    const verify = async (): Promise<void> => {
      try {
        const response = (await apiService.verifyTrainerRegistrationPayment({
          trainer_id: trainerId,
        })) as Record<string, unknown>;

        if (response.Code === 200 && response.payment_status === "completed") {
          // Setup is effectively done from the trainer's perspective — admin
          // still has to approve, but they can now enter the portal.
          localStorage.setItem("trainerProfileCompleted", "true");
          localStorage.removeItem("trainerSetupDraft");
        } else {
          console.warn("Payment verification returned unexpected response:", response);
          // Continue to the confirmation screen anyway — the Stripe webhook is the
          // source of truth and may not have fired yet.
        }
      } catch (err) {
        console.error("Error verifying trainer payment:", err);
      } finally {
        setVerifying(false);
      }
    };

    verify();
  }, [searchParams]);

  const handleContinue = (): void => {
    navigate("/trainer");
  };

  if (verifying) {
    return (
      <div className="font-sans bg-background min-h-screen flex items-center justify-center p-5">
        <div className="text-foreground text-xl">Processing payment…</div>
      </div>
    );
  }

  return (
    <div className="font-sans bg-background min-h-screen flex flex-col">
      <header className="bg-card text-foreground py-5 shadow-sm border-b border-border">
        <div className="max-w-[1200px] mx-auto px-3 sm:px-5 flex items-center gap-4">
          <img src="/images/logo.png" alt="Pheefee Logo" className="w-10 h-10 object-contain" />
          <span className="text-2xl font-bold text-foreground">Pheefee</span>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-5">
        <div className="bg-card rounded-2xl border border-border shadow-lg max-w-[640px] w-full p-8 sm:p-10 text-center flex flex-col gap-6">
          <div className="text-5xl">🎉</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
            Thank you for submitting!
          </h1>
          <p className="text-base leading-relaxed text-muted-foreground">
            Background checks are usually processed in <strong>2–5 business days</strong>. During
            this time, we may also verify your listed credentials and provided insurance
            information. Please watch your email for updates.
          </p>
          <p className="text-base leading-relaxed text-muted-foreground">
            You may log in and edit your portal at any time.
          </p>
          {error && (
            <div className="bg-destructive/10 border border-destructive/25 text-destructive p-4 rounded-xl text-sm">
              {error}
            </div>
          )}
          <div className="flex justify-center">
            <Button onClick={handleContinue} size="md" className="font-semibold rounded-lg">
              Continue to Your Portal →
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainerPaymentSuccess;
